import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Gem, LogOut } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { NAV_GROUPS, findNavItem, navTestId } from "./nav";
import { ServerPicker } from "./ServerPicker";

const KEY = "crank_nav_collapsed";

function NavGroup({ group, open, onToggle, onNavigate, plan }) {
  return (
    <div>
      <button type="button" onClick={onToggle} data-testid={`nav-group-${group.id}`} aria-expanded={open}
        className="w-full flex items-center justify-between px-3 pt-4 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500 hover:text-zinc-300 transition-colors">
        {group.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="space-y-0.5">
          {group.items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={navTestId(n.label)}
              className={({ isActive }) => `group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-volt/10 text-volt font-medium" : "text-zinc-400 hover:text-white hover:bg-zinc-900"}`}>
              {({ isActive }) => (<>
                {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-volt" />}
                <n.icon className="w-[18px] h-[18px] shrink-0" />
                <span className="truncate">{n.label}</span>
                {n.premium && plan === "free" && <Gem className="ml-auto w-3.5 h-3.5 text-volt/80" aria-label="Premium" />}
              </>)}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function SidebarContent({ guild, guilds, onNavigate, onLogout }) {
  const loc = useLocation();
  const sub = loc.pathname.split("/").slice(4).join("/");
  const activeGroup = findNavItem(sub)?.group.id || "general";
  const [collapsed, setCollapsed] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; } });
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(collapsed)); }, [collapsed]);
  const toggle = (id) => setCollapsed((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 h-16 flex items-center gap-2 font-head font-black text-lg shrink-0">
        <BrandLogo className="w-7 h-7" /> crank<span className="text-volt">.live</span>
      </div>
      <div className="px-3 pb-2"><ServerPicker guild={guild} guilds={guilds} /></div>
      <nav className="flex-1 overflow-y-auto px-3 pb-4" aria-label="Dashboard">
        {NAV_GROUPS.map((g) => (
          <NavGroup key={g.id} group={g} plan={guild?.plan} onNavigate={onNavigate}
            open={g.id === activeGroup || !collapsed.includes(g.id)} onToggle={() => toggle(g.id)} />
        ))}
      </nav>
      <div className="p-3 border-t border-zinc-800 space-y-2 shrink-0">
        {guild?.plan === "free" && (
          <NavLink to="billing" onClick={onNavigate} data-testid="sidebar-upgrade-card" className="block rounded-xl border border-volt/30 bg-volt/5 p-3 hover:bg-volt/10 transition-colors">
            <div className="flex items-center gap-2 text-sm font-semibold text-volt"><Gem className="w-4 h-4" /> Upgrade this server</div>
            <div className="text-xs text-zinc-500 mt-0.5">Unlock premium modules and higher limits.</div>
          </NavLink>
        )}
        <button data-testid="dash-logout" onClick={onLogout} className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-zinc-900 w-full transition-colors">
          <LogOut className="w-[18px] h-[18px]" /> Log out
        </button>
      </div>
    </div>
  );
}
